import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { api } from './utils/api';
import { applyBranding, clearBranding } from './utils/branding';
import { analytics } from './utils/analytics';
import AdminDashboard from './pages/AdminDashboard';
import ParentDashboard from './pages/ParentDashboard';
import ChildProfile from './pages/ChildProfile';
import StaffDashboard from './pages/StaffDashboard';
import DriverDashboard from './pages/DriverDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import Login from './pages/Login';
import NurseryOwnerSignupWizard from './pages/NurseryOwnerSignupWizard';
import InviteAccept from './pages/InviteAccept';
import RecapShare from './pages/RecapShare';
import Pricing from './pages/Pricing';
import BillingSuccess from './pages/BillingSuccess';
import LandingPage from './pages/LandingPage';
import StatusPage from './pages/StatusPage';
import WelcomePage from './pages/WelcomePage';
import { ForgotPassword, ResetPassword } from './pages/ForgotPassword';
import StaffIDVerify from './pages/StaffIDVerify';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiePolicy from './pages/legal/CookiePolicy';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { OwnerOnboardingWizard } from './components/OwnerOnboardingWizard';
import { ImpersonationBanner } from './pages/SuperAdminCommandCenter';
import { Toaster } from './components/ui/sonner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  // Password-change modal state. `forced` is true when the backend has
  // flagged the user with `must_change_password` (after a super-admin
  // reset) — in that mode the modal blocks the rest of the UI until a
  // new password is chosen.
  const [showChangePassword, setShowChangePassword] = useState(false);
  // Onboarding wizard visibility. Populated on login/hydration from
  // `GET /api/onboarding/status.should_show`. The wizard is a one-time
  // overlay for newly-provisioned org_owners; dismissal persists on
  // the server so it never resurfaces.
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Load logged-in user on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.getMe()
        .then(userData => {
          setUser(userData);
          // Attach the analytics identity as early as possible so any
          // subsequent event lands on this person, not a fresh anon id.
          analytics.identify(userData);
          // Mirror to localStorage so other tabs / pages (e.g. /pricing)
          // can detect the session synchronously without an API call.
          try {
            localStorage.setItem('user', JSON.stringify({
              _id: userData._id,
              email: userData.email,
              role: userData.role,
              full_name: userData.full_name,
              organization_id: userData.organization_id,
              organization_slug: userData.organization_slug,
            }));
          } catch { /* ignore */ }
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Global 402 PLAN_LIMIT_REACHED interceptor.
  // Wraps window.fetch once at app boot so any backend response with a
  // `PLAN_LIMIT_REACHED` payload fires a window event the dashboard
  // listens for to pop the UpgradeModal. Cheap and works for endpoints
  // we haven't migrated to the new helpers yet.
  useEffect(() => {
    const original = window.fetch;
    if (window.__kwPlanLimitTrapInstalled) return;
    window.__kwPlanLimitTrapInstalled = true;
    window.fetch = async (...args) => {
      const res = await original(...args);
      if (res.status === 402) {
        try {
          const cloned = res.clone();
          const body = await cloned.json();
          const detail = body && body.detail;
          if (detail && detail.error_code === 'PLAN_LIMIT_REACHED') {
            window.dispatchEvent(new CustomEvent('kw:plan-limit-reached', { detail }));
          }
        } catch { /* not JSON — ignore */ }
      }
      return res;
    };
  }, []);

  // Once user is known (not super admin), load their org + apply branding
  useEffect(() => {
    if (!user) {
      setOrganization(null);
      return;
    }
    if (user.role === 'super_admin') {
      // Super admin keeps the neutral platform branding
      clearBranding();
      applyBranding({ primary_color: '#00BCD4', secondary_color: '#FFC107' });
      return;
    }

    api.getMyOrganization()
      .then((org) => {
        setOrganization(org);
        const s = (org && org.settings) || {};
        applyBranding({
          primary_color: s.primary_color,
          secondary_color: s.secondary_color,
          logo_url: s.logo_url,
          welcome_message: s.welcome_message,
          tagline: s.tagline,
          name: org?.name,
          slug: org?.slug,
        });
      })
      .catch(() => {
        // Non-fatal: keep default Kids WiseApp branding
      });
  }, [user]);

  // If the user must change their password (forced reset by super-admin),
  // open the modal automatically and keep it open until the change succeeds.
  useEffect(() => {
    if (user && user.must_change_password) {
      setShowChangePassword(true);
    }
  }, [user]);

  // First-run onboarding wizard for freshly-provisioned org owners.
  // We wait for the forced-password change to complete before showing
  // it — bothering someone with a wizard while they're still trying
  // to log in properly is bad UX.
  useEffect(() => {
    if (!user) return;
    if (user.must_change_password) return;
    // Only owners (or org_admin) ever see the wizard.
    if (!['org_owner', 'org_admin', 'admin'].includes(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getOnboardingStatus();
        if (!cancelled && s?.should_show) setShowOnboarding(true);
      } catch { /* silent — the wizard is a delight, not a critical path */ }
    })();
    return () => { cancelled = true; };
  }, [user, user?.must_change_password]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    // Persist a small snapshot of the user too so cross-page features
    // (e.g. Pricing.js' "Logged-in admin → start Mollie Checkout"
    // branch) can detect session state without re-fetching `/me`.
    try {
      localStorage.setItem('user', JSON.stringify({
        _id: userData._id,
        email: userData.email,
        role: userData.role,
        full_name: userData.full_name,
        organization_id: userData.organization_id,
        organization_slug: userData.organization_slug,
      }));
    } catch { /* localStorage may be full / disabled — ignore */ }
    setUser(userData);
    // Identify to PostHog immediately on fresh login. This attaches
    // this session (and every subsequent event) to the user record.
    analytics.identify(userData);
  };

  const handleLogout = () => {
    // Capture the org slug *before* we wipe state so we can redirect the
    // user back to their branded login page (e.g. /login/sunshine-nursery)
    // instead of the generic /login. This keeps logo/colors consistent and
    // makes "switch user" feel like staying inside the same workspace.
    const slug = organization?.slug || user?.organization_slug || null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearBranding();
    setUser(null);
    setOrganization(null);
    // Detach the analytics identity so the next visitor on this browser
    // doesn't inherit the previous user's PostHog person profile.
    analytics.reset();
    // Use window.location so the branded login page reloads cleanly with
    // its own branding fetch — React state has already been wiped above.
    if (slug) {
      window.location.assign(`/login/${slug}`);
    } else {
      window.location.assign('/login');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading Kids WiseApp...</p>
      </div>
    );
  }

  const sharedDashboardProps = {
    user,
    organization,
    onLogout: handleLogout,
    onChangePassword: () => setShowChangePassword(true),
  };

  return (
    <Router>
      <div className="app-container" data-theme={user ? (user.role === 'parent' ? 'parent' : 'admin') : 'admin'}>
        <ImpersonationBanner />
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/login/:slug"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          {/*
            Public workspace invite acceptance landing page. Reachable
            without auth because the recipient must be able to open it
            from an email before they have an account. The token in the
            URL is the bearer of trust; the page collects join details
            and on success logs the user in and redirects to "/".
          */}
          <Route
            path="/invite/:token"
            element={!user ? <InviteAccept onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          {/*
            Public, no-auth event recap landing page. Reachable from any
            email forwarded by parents to family/friends. Doesn't require
            authentication — the share token in the URL is the bearer of
            trust and the backend gates expiry + visibility. Rendered
            even when a user is logged in so they can preview the link
            they're about to share.
          */}
          <Route path="/share/event-recap/:token" element={<RecapShare />} />
          {/*
            Self-service password reset. Both routes are public (the
            forgot page collects an email; the reset page consumes a
            single-use token). Authed users can still hit them — useful
            if e.g. an admin received a reset link for a sibling
            account while logged into the main one.
          */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Public staff-ID verification page (linked from QR codes). */}
          <Route path="/verify/staff-id/:token" element={<StaffIDVerify />} />
          {/* Public per-org branded marketing landing page. Anyone can
              open this without auth; a signup form on the page lands
              prospects in the nursery's Pending Parents queue. */}
          <Route path="/welcome/:slug" element={<WelcomePage />} />
          {/* Public marketing pricing page — accessible regardless of auth. */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/status" element={<StatusPage />} />

          {/*
            Dedicated nursery-owner signup wizard. Distinct from /login
            (parent + staff sign-in) to remove the intent collision the
            old three-tab layout caused. Accepts ?plan=<key>&interval=<...>
            query params from Pricing.js so the plan card the visitor
            clicked pre-fills at step 1.
          */}
          <Route
            path="/register-your-nursery"
            element={!user ? <NurseryOwnerSignupWizard onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          {/* Legacy /register alias — keeps any external "start free trial"
              links (marketing emails, screenshots, etc.) working. */}
          <Route
            path="/register"
            element={!user ? <NurseryOwnerSignupWizard onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          {/* Public legal pages. Indexable and linked from every
              marketing + auth footer. The pages themselves carry a
              very visible "Draft scaffold" banner until the owner has
              had them reviewed by a UK-childcare-aware solicitor. */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          {/* Mollie Checkout return URL — confirms subscription and routes
              the user to the Billing tab. Requires auth so we can fetch
              the session by id and tie it back to their org. */}
          <Route
            path="/billing/success"
            element={user ? <BillingSuccess /> : <Navigate to="/login" />}
          />
          <Route
            path="/"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <SuperAdminDashboard {...sharedDashboardProps} />
                ) : (
                  // Admin tier — org_owner / org_admin / deputy_manager
                  // all share the AdminDashboard. The legacy "admin"
                  // role is kept for back-compat with older accounts.
                  ['admin', 'org_owner', 'org_admin', 'deputy_manager'].includes(user.role) ? (
                    <AdminDashboard {...sharedDashboardProps} />
                  ) : user.role === 'driver' ? (
                    <DriverDashboard {...sharedDashboardProps} />
                  ) : (
                    // Staff tier — base staff and room_leader currently
                    // share the StaffDashboard; room-scoped admin
                    // affordances are surfaced inside the dashboard
                    // itself (e.g. "Approve room timesheets" only
                    // appears for room_leader).
                    ['staff', 'room_leader'].includes(user.role) ? (
                      <StaffDashboard {...sharedDashboardProps} />
                    ) : (
                      <ParentDashboard {...sharedDashboardProps} />
                    )
                  )
                )
              ) : (
                // Un-authenticated visitors land on the marketing page.
                // /login remains accessible directly via the navbar and CTAs.
                <LandingPage />
              )
            }
          />
          <Route path="/child/:childId" element={user ? <ChildProfile /> : <Navigate to="/login" />} />
          {/*
            Branded short-URL pattern: `kidswiseapp.com/{org-slug}`.
            Lets organisations share a memorable URL with their parents
            (printed on flyers, sent in emails, saved as bookmarks)
            that drops the visitor straight onto their branded login
            page without the extra `/login/` prefix.

            How resolution works:
              • The static routes above (e.g. /login, /pricing,
                /forgot-password, /verify/*, /share/*, /invite/*,
                /billing/*) ALWAYS win — React Router v6 prefers static
                segments over dynamic ones, so this catch-all never
                shadows them.
              • For everything else the bare slug, the <Login /> component
                reads the `slug` URL param via `useParams()` and fetches
                org branding. If the slug doesn't match an org it falls
                back silently to default branding (existing behavior of
                /login/:slug) — so a typo still produces a usable page
                rather than a blank screen.
              • Authenticated users skip the login UI and route to their
                normal dashboard at "/", mirroring how /login/:slug
                behaves today.
          */}
          <Route
            path="/:slug"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
        </Routes>
        {/*
          Global password-change modal.
          - Available to every authenticated user via the dashboard "Change
            password" button.
          - Auto-opens in `forced` mode when the user has been flagged with
            `must_change_password=true` after a super-admin reset, blocking
            other interactions until they choose a new password.
        */}
        {user && (
          <ChangePasswordModal
            open={showChangePassword}
            forced={!!user.must_change_password}
            onClose={() => setShowChangePassword(false)}
            onSuccess={async () => {
              try {
                const refreshed = await api.getMe();
                setUser(refreshed);
              } catch {
                /* Non-fatal: the user can still keep using the app. */
              }
              setShowChangePassword(false);
            }}
          />
        )}
        {/* Phase K.5 note: the aggressive first-run OwnerOnboardingWizard
            overlay has been retired in favour of the persistent Get
            Started experience (WelcomeHeroCard + GettingStartedChecklist)
            on the admin dashboard. Owners can now poke around freely
            without a blocking modal on first login. The wizard's backend
            endpoints (logo/address/invite-staff) are still live and now
            act as the "Start →" targets for the corresponding checklist
            steps. If we ever want the blocking flow back, re-mounting
            <OwnerOnboardingWizard /> here restores the old behaviour.
        {showOnboarding && (
          <OwnerOnboardingWizard onDone={() => setShowOnboarding(false)} />
        )} */}
      </div>
      {/* Global toast container — used for non-blocking confirmations
          (record deleted, draft saved, copy-to-clipboard etc.). */}
      <Toaster position="top-right" richColors closeButton />
    </Router>
  );
}

export default App;
